import React, { useEffect, useState } from "react";
import { ClockCircleOutlined } from "@ant-design/icons";
import { Button, DatePicker, Form, Input, Select, Space, TimePicker, type FormInstance } from "antd";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useTranslation } from "react-i18next";
import { useDebounce } from "use-debounce";

import { RequestStatus, traineesActions } from "@fitness-app/app-store";
import { TeamMemberTaskCategory, TeamMemberTaskPriority } from "@fitness-app/data-models/entities/TeamMemberTask";

import { useAppDispatch, useAppSelector } from "~/store/initializeStore";
import {
  categoryOptions,
  getPriorityColor,
  priorityOptions,
  type TaskDateTime,
  type TeamMemberTaskFormModel,
  type TraineeOption,
} from "../types";

dayjs.extend(utc);
dayjs.extend(timezone);

const DEFAULT_TIMEZONE = "Europe/Warsaw";

interface TeamMemberTaskFormProps {
  formController?: FormInstance<TeamMemberTaskFormModel>;
  onSubmit: (formData: TeamMemberTaskFormModel) => void;
  model?: Partial<TeamMemberTaskFormModel> | null;
}

const TeamMemberTaskForm: React.FC<TeamMemberTaskFormProps> = ({ formController, onSubmit, model }) => {
  const { t } = useTranslation("dashboard");
  const dispatch = useAppDispatch();
  const trainees = useAppSelector((state) => state.trainees.list);
  const traineesLoading = useAppSelector((state) => state.trainees.listStatus === RequestStatus.SUBSCRIBING);
  const [showTime, setShowTime] = useState(() => Boolean(model?.dueDate?.time));
  const [traineeSearchValue, setTraineeSearchValue] = useState("");
  const [debouncedSearchValue, { isPending }] = useDebounce(traineeSearchValue, 300);
  const [initialTrainee, setInitialTrainee] = useState<TraineeOption | null>(null);

  // Store initial trainee from model
  useEffect(() => {
    if (model?.trainee) {
      setInitialTrainee(model.trainee);
    }
  }, [model?.trainee]);

  // Fetch trainees when search value changes
  useEffect(() => {
    dispatch(traineesActions.setSearchTerm(debouncedSearchValue));
    dispatch(traineesActions.fetchTrainees());

    return () => {
      dispatch(traineesActions.setSearchTerm(""));
    };
  }, [debouncedSearchValue, dispatch]);

  // Build options list, ensuring initial trainee is always included
  const traineeOptions = React.useMemo(() => {
    const options: Array<{ value: string; label: string; description?: string }> = trainees.map((trainee) => ({
      value: trainee.id,
      label: `${trainee.firstName} ${trainee.lastName}`,
      description: trainee.email,
    }));

    // Add initial trainee if not in list
    if (initialTrainee && !trainees.some((t) => t.id === initialTrainee.id)) {
      options.unshift({
        value: initialTrainee.id,
        label: `${initialTrainee.firstName ?? ""} ${initialTrainee.lastName ?? ""}`.trim(),
        description: initialTrainee.email ?? undefined,
      });
    }

    return options;
  }, [trainees, initialTrainee]);

  useEffect(() => {
    if (model) {
      setShowTime(Boolean(model.dueDate?.time));
      formController?.setFieldsValue({
        ...model,
        tags: model.tags || [],
      });
    } else {
      formController?.resetFields();
      setShowTime(false);
    }
  }, [model, formController]);

  const handleDateChange = (date: dayjs.Dayjs | null) => {
    const current = formController?.getFieldValue("dueDate") as TaskDateTime | null;
    formController?.setFieldValue("dueDate", {
      date: date ? date.format("YYYY-MM-DD") : null,
      time: current?.time || null,
      timezone: current?.timezone || DEFAULT_TIMEZONE,
    });
  };

  const handleTimeChange = (time: dayjs.Dayjs | null) => {
    const current = formController?.getFieldValue("dueDate") as TaskDateTime | null;
    formController?.setFieldValue("dueDate", {
      date: current?.date || null,
      time: time ? time.format("HH:mm") : null,
      timezone: current?.timezone || DEFAULT_TIMEZONE,
    });
  };

  const toggleTimeField = () => {
    const newShowTime = !showTime;
    setShowTime(newShowTime);

    if (!newShowTime) {
      // Clear time when hiding
      const current = formController?.getFieldValue("dueDate") as TaskDateTime | null;
      if (current) {
        formController?.setFieldValue("dueDate", {
          ...current,
          time: null,
        });
      }
    }
  };

  const dueDateValue = Form.useWatch("dueDate", formController) as TaskDateTime | null;

  return (
    <Form
      name="team-member-task-form"
      layout="vertical"
      form={formController}
      initialValues={{
        priority: TeamMemberTaskPriority.Medium,
        category: TeamMemberTaskCategory.Other,
        tags: [],
        dueDate: { date: dayjs().format("YYYY-MM-DD"), time: null, timezone: DEFAULT_TIMEZONE },
      }}
      onFinish={onSubmit}
    >
      <Form.Item
        name="title"
        label={t("tasks.taskTitle")}
        rules={[{ required: true, message: t<string>("tasks.taskTitleRequired") }]}
      >
        <Input placeholder={t<string>("tasks.taskTitlePlaceholder")} autoFocus />
      </Form.Item>

      <Form.Item name="description" label={t<string>("tasks.taskDescription")}>
        <Input.TextArea placeholder={t<string>("tasks.descriptionPlaceholder")} rows={3} />
      </Form.Item>

      <Form.Item name="dueDate" label="Termin" className="mb-0 h-[30px]">
        <Input type="hidden" />
      </Form.Item>

      <div className="mb-4 flex items-center gap-2">
        <DatePicker
          style={{ width: showTime ? 140 : 200 }}
          placeholder={t<string>("tasks.datePlaceholder")}
          format="YYYY-MM-DD"
          value={dueDateValue?.date ? dayjs(dueDateValue.date) : null}
          onChange={handleDateChange}
        />

        {showTime && (
          <TimePicker
            style={{ width: 100 }}
            placeholder={t<string>("tasks.timePlaceholder")}
            format="HH:mm"
            minuteStep={5}
            value={dueDateValue?.time ? dayjs(dueDateValue.time, "HH:mm") : null}
            onChange={handleTimeChange}
          />
        )}

        <Button
          icon={<ClockCircleOutlined />}
          onClick={toggleTimeField}
          type={showTime ? "primary" : "default"}
          title={showTime ? t<string>("tasks.removeTime") : t<string>("tasks.addTime")}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Form.Item name="priority" label={t("tasks.priorityLabel")}>
          <Select placeholder={t("tasks.selectPriorityPlaceholder")}>
            {priorityOptions.map((option) => (
              <Select.Option key={option.value} value={option.value}>
                <Space>
                  <span
                    style={{
                      display: "inline-block",
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: getPriorityColor(option.value),
                    }}
                  />
                  {option.label}
                </Space>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="category" label={t("tasks.categoryLabel")}>
          <Select placeholder={t("tasks.selectCategoryPlaceholder")} options={categoryOptions} />
        </Form.Item>
      </div>

      <Form.Item name="traineeId" label={t("tasks.traineeLabel")}>
        <Select
          placeholder={t("tasks.searchTraineePlaceholder")}
          allowClear
          showSearch
          filterOption={false}
          loading={traineesLoading || isPending()}
          onSearch={setTraineeSearchValue}
          notFoundContent={traineeSearchValue ? t("tasks.noSearchResults") : t("tasks.typeToSearch")}
          optionRender={(option) => (
            <div className="flex flex-col">
              <span>{option.label}</span>
              {option.data.description && <span className="text-xs text-gray-400">{option.data.description}</span>}
            </div>
          )}
          options={traineeOptions}
        />
      </Form.Item>

      <Form.Item name="tags" label={t("tasks.addTagsPlaceholder")} hidden>
        <Select mode="tags" placeholder={t("tasks.addTagsPlaceholder")} tokenSeparators={[","]} />
      </Form.Item>

      {/* <Form.List name="reminders" >
        {(fields, { add, remove }) => (
          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium">Przypomnienia</span>
              <Button
                type="dashed"
                size="small"
                onClick={() => add({ id: crypto.randomUUID(), date: null, sent: false })}
                icon={<PlusOutlined />}
              >
                Dodaj przypomnienie
              </Button>
            </div>
            {fields.map(({ key, name, ...restField }) => (
              <div key={key} className="mb-2 flex items-center gap-2">
                <Form.Item {...restField} name={[name, "id"]} hidden>
                  <Input />
                </Form.Item>
                <Form.Item {...restField} name={[name, "sent"]} hidden initialValue={false}>
                  <Input />
                </Form.Item>
                <Form.Item
                  {...restField}
                  name={[name, "date"]}
                  className="mb-0 flex-1"
                  rules={[{ required: true, message: "Data przypomnienia jest wymagana" }]}
                  getValueProps={(value) => ({ value: value ? dayjs(value) : null })}
                  getValueFromEvent={(date) => (date ? date.format("YYYY-MM-DDTHH:mm:ss") : null)}
                >
                  <DatePicker
                    showTime
                    format="YYYY-MM-DD HH:mm"
                    placeholder="Data i godzina przypomnienia"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
                <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)} />
              </div>
            ))}
          </div>
        )}
      </Form.List> */}
    </Form>
  );
};

export default TeamMemberTaskForm;
